import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastSuccess } from "@/lib/toast";

const ADMIN_CAPABILITIES = [
  {
    key: "org.invite",
    label: "Invite to org",
    description: "Send organization invitations",
  },
  {
    key: "org.create_teams",
    label: "Create teams",
    description: "Create new teams in this organization",
  },
  {
    key: "org.post",
    label: "Org-wide posts",
    description: "Post organization-wide and mark posts critical (org-wide or any team)",
  },
  {
    key: "org.remove_members",
    label: "Remove members",
    description: "Remove other members (not the owner)",
  },
  {
    key: "org.manage_settings",
    label: "Manage settings",
    description: "Edit organization name, description, and logo",
  },
  {
    key: "org.integrations",
    label: "Integrations",
    description:
      "Install Circatrons, set up webhooks, and delete bot messages",
  },
  {
    key: "org.personal_bots",
    label: "Personal bots",
    description:
      "Create and manage own personal Circatron installs when the org allows them",
  },
  {
    key: "org.social_publish",
    label: "Social publish",
    description: "Post officially as the organization in Community Space",
  },
];

/** Same keys members can be granted (manage_settings is admin-default only). */
const MEMBER_GRANTS = ADMIN_CAPABILITIES.filter(
  (c) => c.key !== "org.manage_settings"
);

const LEADERSHIP_GRANTS = ["org.invite", "org.post"];

function isLeadershipGrants(grants) {
  if (!Array.isArray(grants) || grants.length !== LEADERSHIP_GRANTS.length) {
    return false;
  }
  const set = new Set(grants);
  return LEADERSHIP_GRANTS.every((k) => set.has(k));
}

/**
 * Manage org member role / grants / denies.
 * Admins: role + grants on non-owners. Owner: deny any admin default capability; transfer.
 */
const ManageOrgMemberDialog = ({
  isOpen,
  onClose,
  orgId,
  accessToken,
  member,
  actorIsOwner,
  canManageRoles,
  canRemoveMembers,
  onSuccess,
}) => {
  const confirm = useConfirm();
  const [role, setRole] = useState("member");
  const [grants, setGrants] = useState([]);
  /** Admin defaults that are denied (off). Enabled = not in this set. */
  const [denies, setDenies] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ssoBound, setSsoBound] = useState(false);

  const targetIsOwner = Boolean(member?.is_owner);
  const targetUserId = member?.user?.user_id;

  useEffect(() => {
    if (!member || !isOpen) return;
    setRole(member.role === "admin" ? "admin" : "member");
    setGrants(Array.isArray(member.grants) ? [...member.grants] : []);
    setDenies(Array.isArray(member.denies) ? [...member.denies] : []);
    setSsoBound(Boolean(member.sso_bound));
    setError(null);
  }, [member, isOpen]);

  const applyPreset = (preset) => {
    if (preset === "admin") {
      setRole("admin");
      setGrants([]);
      setDenies([]);
      return;
    }
    if (preset === "invite_post" || preset === "leadership") {
      setRole("member");
      setGrants([...LEADERSHIP_GRANTS]);
      setDenies([]);
      return;
    }
    setRole("member");
    setGrants([]);
    setDenies([]);
  };

  const activePreset =
    role === "admin"
      ? "admin"
      : isLeadershipGrants(grants)
        ? "invite_post"
        : "member";

  const toggleGrant = (key) => {
    setGrants((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleAdminCapability = (key) => {
    // Toggle off → add deny; toggle on → remove deny
    setDenies((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSave = async () => {
    if (!targetUserId || targetIsOwner) return;
    setBusy(true);
    setError(null);
    try {
      const body = { role };
      if (role === "member") {
        body.grants = grants;
      } else if (actorIsOwner) {
        // Admins use denies only — never send grants
        body.denies = denies;
      }
      await axios.patch(
        `${API_BASE_URL}/org/${orgId}/members/${targetUserId}`,
        body,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to update member"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!targetUserId || targetIsOwner || !canRemoveMembers) return;
    const ok = await confirm({
      title: "Remove member?",
      description: `Remove ${member?.user?.first_name || "this member"} from the organization?`,
      confirmLabel: "Remove",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(
        `${API_BASE_URL}/org/${orgId}/members/${targetUserId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to remove member"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleUnlinkIdp = async () => {
    if (!targetUserId || !canRemoveMembers || !ssoBound) return;
    const ok = await confirm({
      title: "Unlink IdP?",
      description: `Remove the identity-provider link for ${
        member?.user?.first_name || "this member"
      }. They must complete SSO hop again on next enter if SSO is enabled.`,
      confirmLabel: "Unlink IdP",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/bindings/${encodeURIComponent(targetUserId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setSsoBound(false);
      toastSuccess("IdP link removed");
      onSuccess?.();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to unlink IdP"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleTransfer = async () => {
    if (!targetUserId || !actorIsOwner || targetIsOwner) return;
    const ok = await confirm({
      title: "Transfer ownership?",
      description: `Transfer ownership to ${member?.user?.first_name || "this member"}? You will remain an admin but no longer be the owner.`,
      confirmLabel: "Transfer",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API_BASE_URL}/org/${orgId}/transfer`,
        { user_id: targetUserId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to transfer ownership"
      );
    } finally {
      setBusy(false);
    }
  };

  const name = member?.user
    ? `${member.user.first_name || ""} ${member.user.last_name || ""}`.trim()
    : "Member";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] !flex flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-3 pr-12">
          <DialogTitle>Member access</DialogTitle>
          <DialogDescription>
            {targetIsOwner
              ? `${name} is the organization owner. Transfer ownership to change this.`
              : `Set role and access for ${name}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2 space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {targetIsOwner ? (
            <p className="text-sm text-muted-foreground">
              The owner always has full organization capabilities.
            </p>
          ) : (
            <>
              {canManageRoles && (
                <div className="space-y-2">
                  <Label>Preset</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      {
                        value: "member",
                        label: "Member",
                        hint: "Standard access",
                      },
                      {
                        value: "invite_post",
                        label: "Invite + post",
                        hint: "Invite people and post org-wide",
                      },
                      {
                        value: "admin",
                        label: "Admin",
                        hint: "Full admin defaults",
                      },
                    ].map((option) => {
                      const selected = activePreset === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={busy}
                          onClick={() => applyPreset(option.value)}
                          className={cn(
                            "rounded-md border px-3 py-3 text-left transition-colors",
                            selected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:bg-muted/50"
                          )}
                        >
                          <p className="text-sm font-medium">{option.label}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {option.hint}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {canManageRoles && role === "member" && (
                <div className="space-y-2">
                  <Label>Additional access</Label>
                  <div className="space-y-2">
                    {MEMBER_GRANTS.map((grant) => {
                      const enabled = grants.includes(grant.key);
                      return (
                        <button
                          key={grant.key}
                          type="button"
                          disabled={busy}
                          onClick={() => toggleGrant(grant.key)}
                          className={cn(
                            "flex w-full items-start justify-between gap-3 rounded-md border px-3 py-3 text-left transition-colors",
                            enabled
                              ? "border-primary bg-primary/5"
                              : "border-border hover:bg-muted/50"
                          )}
                        >
                          <div>
                            <p className="text-sm font-medium">{grant.label}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {grant.description}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {enabled ? "On" : "Off"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {role === "admin" && (
                <div className="space-y-2">
                  <Label>Admin access</Label>
                  <p className="text-xs text-muted-foreground">
                    {actorIsOwner
                      ? "Defaults for admins. Turn off any capability to revoke it."
                      : "Defaults for admins. Only the owner can revoke these."}
                  </p>
                  <div className="space-y-2">
                    {ADMIN_CAPABILITIES.map((cap) => {
                      const enabled = !denies.includes(cap.key);
                      const canEdit = actorIsOwner && !busy;
                      return (
                        <button
                          key={cap.key}
                          type="button"
                          disabled={!canEdit}
                          onClick={() =>
                            canEdit && toggleAdminCapability(cap.key)
                          }
                          className={cn(
                            "flex w-full items-start justify-between gap-3 rounded-md border px-3 py-3 text-left transition-colors",
                            enabled
                              ? "border-primary bg-primary/5"
                              : "border-border hover:bg-muted/50",
                            !actorIsOwner && "opacity-80 cursor-default"
                          )}
                        >
                          <div>
                            <p className="text-sm font-medium">{cap.label}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {cap.description}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {enabled ? "On" : "Off"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-col sm:flex-row gap-2 border-t bg-background px-6 py-4">
          <div className="flex flex-wrap gap-2 sm:mr-auto">
            {canRemoveMembers && ssoBound && (
              <Button
                variant="outline"
                onClick={handleUnlinkIdp}
                disabled={busy}
              >
                Unlink IdP
              </Button>
            )}
            {!targetIsOwner && canRemoveMembers && (
              <Button
                variant="destructive"
                onClick={handleRemove}
                disabled={busy}
              >
                Remove
              </Button>
            )}
          </div>
          {!targetIsOwner && actorIsOwner && (
            <Button variant="outline" onClick={handleTransfer} disabled={busy}>
              Transfer ownership
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {!targetIsOwner &&
            (canManageRoles || (actorIsOwner && role === "admin")) && (
              <Button onClick={handleSave} disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </Button>
            )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageOrgMemberDialog;
